// src/i18n/lang.ts
//
// Idioma escolhido, sem depender de React.
//
// Mora fora do contexto porque o interceptor do axios precisa ler o idioma
// no momento da requisição, e lá não existe hook. O contexto em i18n/index
// espelha este valor para a interface reagir à troca.

export type Lang = "pt_BR" | "en_US";

export const LANGS: Lang[] = ["pt_BR", "en_US"];

export const DEFAULT_LANG: Lang = "pt_BR";

/** Rótulo curto para o botão. */
export const LANG_LABEL: Record<Lang, string> = {
  pt_BR: "PT",
  en_US: "EN",
};

/** Nome por extenso, no próprio idioma. */
export const LANG_NAME: Record<Lang, string> = {
  pt_BR: "Português",
  en_US: "English",
};

const STORAGE_KEY = "lang";

const ehValido = (valor: unknown): valor is Lang =>
  typeof valor === "string" && (LANGS as string[]).includes(valor);

/**
 * Idioma da sessão, em memória.
 *
 * O localStorage é só persistência entre visitas; a verdade durante a
 * sessão mora aqui. O motivo é um bug real: a interface lê o idioma do
 * estado do React (I18nProvider, inicializado uma vez na montagem) e o
 * interceptor do axios lia do localStorage a cada requisição. Qualquer
 * localStorage.clear() no meio do caminho — e existem cinco, nos guards de
 * rota e no Logout — dessincronizava os dois: a tela continuava em inglês e
 * as chamadas voltavam a sair em pt_BR, em silêncio. Era exatamente a cara
 * do checkout da Stripe abrindo em português com o painel em inglês.
 *
 * null = ainda não lido nesta sessão.
 */
let idiomaDaSessao: Lang | null = null;

/**
 * Idioma em uso. Cai no padrão quando nada foi escolhido ou quando o
 * navegador bloqueia o localStorage (modo privado).
 */
export const getLang = (): Lang => {
  if (idiomaDaSessao !== null) return idiomaDaSessao;

  try {
    const salvo = localStorage.getItem(STORAGE_KEY);
    idiomaDaSessao = ehValido(salvo) ? salvo : DEFAULT_LANG;
  } catch {
    idiomaDaSessao = DEFAULT_LANG;
  }

  return idiomaDaSessao;
};

export const setLang = (lang: Lang): void => {
  idiomaDaSessao = lang;

  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    /* sem persistência: a troca vale só para esta sessão */
  }
};

/**
 * Esquece a escolha, para o Logout chamar junto com o localStorage.clear().
 *
 * Sem isto o idioma de um usuário continuaria valendo para o próximo que
 * entrasse sem recarregar a página — e o painel admin, que não tem seletor,
 * deve voltar ao português. O Logout de verdade faz window.location.href,
 * que recarrega e zeraria o módulo de qualquer jeito; os guards de rota
 * usam <Navigate>, que NÃO recarrega, e é para esses que isto existe.
 */
export const resetLang = (): void => {
  idiomaDaSessao = null;

  // apaga a chave também, para não depender de quem chama lembrar do
  // localStorage.clear() antes — senão o getLang() seguinte releria o
  // idioma antigo de volta do disco e o reset não teria servido de nada
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nada a remover */
  }
};